import React, { useState, useEffect } from 'react';
import { FiList, FiCheck, FiX, FiRotateCcw, FiMove, FiAlertCircle } from 'react-icons/fi';

const OrderingQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {

  const [orderedItems, setOrderedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // Initialize ordered items from current answer or default order
  useEffect(() => {
    if (currentAnswer && currentAnswer.orderedItems) {
      setOrderedItems(currentAnswer.orderedItems);
    } else if (question.options) {
      // Initialize with default order (as received from backend)
      const initialOrder = [...question.options].sort((a, b) => 
        (a.sortOrder || 0) - (b.sortOrder || 0)
      );
      setOrderedItems(initialOrder);
    }
  }, [currentAnswer, question.options]);

  const handleDragStart = (e, item) => {
    if (isSubmitted) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (isSubmitted) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    if (isSubmitted || !draggedItem) return;
    e.preventDefault();

    const draggedIndex = orderedItems.findIndex(item => item.id === draggedItem.id);
    const targetIndex = orderedItems.findIndex(item => item.id === targetItem.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const newOrder = [...orderedItems];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);
      setOrderedItems(newOrder);
    }
    setDraggedItem(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (isSubmitted) return;
    
    const newOrder = [...orderedItems];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setOrderedItems(newOrder);
  };

  const moveItemUp = (index) => {
    if (index > 0) {
      moveItem(index, index - 1);
    }
  };

  const moveItemDown = (index) => {
    if (index < orderedItems.length - 1) {
      moveItem(index, index + 1);
    }
  };

  const resetOrder = () => {
    if (isSubmitted) return;
    
    const resetOrder = [...question.options].sort((a, b) => 
      (a.sortOrder || 0) - (b.sortOrder || 0)
    );
    setOrderedItems(resetOrder);
  };

  const validateOrder = () => {
    if (orderedItems.length === 0) {
      alert('Please arrange the items in the correct order before submitting.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateOrder()) return;

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        orderedItems: orderedItems.map((item, index) => ({
          id: item.id,
          text: item.text,
          userOrder: index + 1,
          correctOrder: item.sortOrder || 0
        })),
        type: 'ORDERING',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting ordering answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getItemStatus = (item, index) => {
    if (!showFeedback || !currentAnswer) return null;
    
    const userOrder = index + 1;
    const correctOrder = item.sortOrder || 0;
    
    if (userOrder === correctOrder) {
      return 'correct';
    } else {
      return 'incorrect';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheck className="text-green-600 text-lg" />;
      case 'incorrect': return <FiX className="text-red-600 text-lg" />;
      default: return null;
    }
  };

  const getCorrectPosition = (item) => {
    if (!showFeedback) return null;
    return item.sortOrder || 0;
  };

  // Handle case where there are no options yet
  if (!question.options || question.options.length === 0) {
    return (
      <div className="ordering-question bg-gray-100 p-6">
        <div className="mb-6">
          {/* Question Text */}
          <div className="text-gray-900 text-base font-bold leading-relaxed mb-4">
            {question.text}
          </div>
          
          <div className="bg-gray-50 p-4">
            <div className="flex items-center gap-2 text-gray-800">
              <FiAlertCircle className="text-lg" />
              <span className="font-medium">Question Preview</span>
            </div>
            <p className="text-gray-700 mt-2">
              This is an ordering question. The question text is displayed above, but the items to arrange are not yet configured. 
              When fully configured, you'll see a list of items that you can drag and drop to reorder.
            </p>
            <div className="mt-3 p-3 bg-white rounded border">
              <p className="text-sm text-gray-600">
                <strong>Question:</strong> {question.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ordering-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <div className="text-gray-900 text-base font-bold leading-relaxed mb-6">
          {question.text}
        </div>


      </div>

      {/* Images if any */}
      {question.images && question.images.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Supporting Materials:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {question.images.map((image, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={image.imageUrl} 
                  alt={image.altText || `Question image ${index + 1}`}
                  className="w-full h-auto max-h-64 object-contain"
                />
                {image.altText && (
                  <div className="p-3 bg-gray-50 text-sm text-gray-600">
                    {image.altText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ordering Interface */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
          Arrange Items in Correct Order
        </h4>
        
        <div className="space-y-3">
          {orderedItems.map((item, index) => {
            const status = getItemStatus(item, index);
            const correctPosition = getCorrectPosition(item);
            
            return (
              <div
                key={item.id}
                draggable={!isSubmitted}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, item)}
                className={`p-4 border-2 rounded-lg transition-all duration-200 cursor-move ${
                  getStatusColor(status)
                } ${draggedItem?.id === item.id ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  {/* Position and Content */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-bold min-w-[2rem] text-center">
                        {index + 1}
                      </span>
                      {showFeedback && correctPosition && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          status === 'correct' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          Correct: {correctPosition}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-gray-800 font-medium">
                        {item.text}
                      </div>
                    </div>
                  </div>

                  {/* Status Icon */}
                  <div className="ml-4">
                    {getStatusIcon(status)}
                  </div>

                  {/* Move Controls */}
                  {!isSubmitted && (
                    <div className="flex items-center gap-1 ml-4">
                      <button
                        onClick={() => moveItemUp(index)}
                        disabled={index === 0}
                        className={`p-1 rounded ${
                          index === 0 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveItemDown(index)}
                        disabled={index === orderedItems.length - 1}
                        className={`p-1 rounded ${
                          index === orderedItems.length - 1 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        ↓
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      {!isSubmitted && (
        <div className="flex justify-center items-center">
          <button
            onClick={resetOrder}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiRotateCcw className="text-sm" />
            Reset to Original Order
          </button>
        </div>
      )}

      {/* Feedback Display */}
      {showFeedback && currentAnswer && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Your Order:</h4>
          <div className="space-y-2">
            {orderedItems.map((item, index) => {
              const status = getItemStatus(item, index);
              const correctPosition = getCorrectPosition(item);
              
              return (
                <div key={item.id} className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-gray-700">Position {index + 1}:</span>
                  <span className="text-gray-600">{item.text}</span>
                  {correctPosition && (
                    <>
                      <span className="text-gray-500">→</span>
                      <span className={`font-medium ${
                        status === 'correct' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Should be at position {correctPosition}
                      </span>
                    </>
                  )}
                  {getStatusIcon(status)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mt-4 text-sm text-gray-600 text-center">
        Items to order: {orderedItems.length}
      </div>
    </div>
  );
};

export default OrderingQuestion; 